import { useEffect, useRef, useState } from "react";
import { XCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SearchFilterBar, type FilterColumn } from "@/components/shared/SearchFilterBar";
import { TableHeadWithTooltip } from "@/components/shared/TableHeadWithTooltip";
import { TablePagination } from "@/components/shared/TablePagination";
import { aiUsageApi } from "@/resources/ai-usage";
import { formatEasternDateTime } from "@/utils";
import type { AiUsageCallStatus, AiUsageLogFilterOptions } from "@/types";
import { formatDuration, formatMoney, formatTokens } from "./ai-usage-format";

// The design's TableHead is semibold foreground; ours is not, so the header carries it (as the Landscape Log does).
const HEADER_CLASS = "text-xs font-semibold text-foreground";
const NUMERIC_HEADER_CLASS = `${HEADER_CLASS} text-right`;
const NUMERIC_CELL_CLASS = "text-xs text-right tabular-nums whitespace-nowrap";
const MUTED_CELL_CLASS = "text-xs text-muted-foreground whitespace-nowrap";
const DASH = "—";

const STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
];

const NO_OPTIONS: AiUsageLogFilterOptions = { models: [], features: [] };

export function AiUsageLogView() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({ model: [], feature: [], status: [] });

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError } = aiUsageApi.useGetAiUsageLogs({
    search: debouncedSearch || undefined,
    model: filters.model,
    feature: filters.feature,
    status: filters.status as AiUsageCallStatus[],
    page: page + 1,
    per_page: pageSize,
  });

  // Each filter change is a new query key, so `data` is briefly undefined; the
  // options are remembered so the open filter panel does not empty under the user.
  const lastOptions = useRef<AiUsageLogFilterOptions>(NO_OPTIONS);
  if (data) lastOptions.current = data.filter_options;
  const options = lastOptions.current;

  // Same narrowing as the Landscape Log: a failed background refetch keeps the stale page.
  const hasLoadError = isError && !data;
  const rows = data?.data ?? [];

  const filterColumns: FilterColumn[] = [
    { id: "model", label: "Model", options: options.models.map((m) => ({ value: m, label: m })) },
    { id: "feature", label: "Feature", options: options.features.map((f) => ({ value: f, label: f })) },
    { id: "status", label: "Status", options: STATUS_OPTIONS },
  ];

  return (
    <div className="space-y-4">
      <SearchFilterBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        placeholder="Search feature, prompt, model, user…"
        searchWidth="flex-1 max-w-xs"
        filterColumns={filterColumns}
        activeFilters={filters}
        onFiltersChange={(next) => {
          setFilters({ model: next.model ?? [], feature: next.feature ?? [], status: next.status ?? [] });
          setPage(0);
        }}
      >
        {!isLoading && !hasLoadError && data && (
          <span className="text-xs text-muted-foreground">{data.total} calls</span>
        )}
      </SearchFilterBar>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={HEADER_CLASS}>Time</TableHead>
              <TableHead className={HEADER_CLASS}>Feature / Prompt</TableHead>
              <TableHead className={HEADER_CLASS}>Model</TableHead>
              <TableHead className={HEADER_CLASS}>User</TableHead>
              <TableHead className={HEADER_CLASS}>Brand</TableHead>
              <TableHeadWithTooltip
                className={NUMERIC_HEADER_CLASS}
                tooltip="Input tokens → output tokens for this call."
              >
                Tokens
              </TableHeadWithTooltip>
              <TableHead className={NUMERIC_HEADER_CLASS}>Duration</TableHead>
              <TableHead className={NUMERIC_HEADER_CLASS}>Cost</TableHead>
              <TableHead className={HEADER_CLASS}>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hasLoadError ? (
              <TableRow>
                <TableCell colSpan={9} className="p-10">
                  <Alert variant="destructive" className="mx-auto max-w-md">
                    <XCircle className="h-4 w-4" aria-hidden="true" />
                    <AlertTitle>Couldn&apos;t load AI usage</AlertTitle>
                    <AlertDescription>
                      Something went wrong loading AI usage. Try again in a moment.
                    </AlertDescription>
                  </Alert>
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={`ai-usage-log-skeleton-${idx}`}>
                  <TableCell colSpan={9}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-sm text-muted-foreground text-center py-8">
                  No AI calls match your filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} data-testid={`ai-usage-log-row-${row.id}`}>
                  <TableCell className={MUTED_CELL_CLASS}>{formatEasternDateTime(row.created_at)}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium text-foreground">{row.feature_area}</div>
                    {row.prompt_label !== row.feature_area && (
                      <div className="text-muted-foreground">{row.prompt_label}</div>
                    )}
                  </TableCell>
                  <TableCell className={MUTED_CELL_CLASS}>{row.model}</TableCell>
                  <TableCell className={MUTED_CELL_CLASS}>{row.user_name ?? "System"}</TableCell>
                  <TableCell className={MUTED_CELL_CLASS}>{row.brand_name ?? DASH}</TableCell>
                  <TableCell className={NUMERIC_CELL_CLASS}>
                    {formatTokens(row.input_tokens, row.output_tokens)}
                  </TableCell>
                  <TableCell className={NUMERIC_CELL_CLASS}>{formatDuration(row.duration_ms)}</TableCell>
                  <TableCell className={`${NUMERIC_CELL_CLASS}${row.unpriced ? " text-muted-foreground" : ""}`}>
                    {row.unpriced ? "Unpriced" : formatMoney(row.cost)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`${
                        row.status === "success" ? "bg-status-success-soft" : "bg-status-error-soft"
                      } text-foreground border-0`}
                    >
                      {row.status === "success" ? "Success" : "Error"}
                    </Badge>
                    {row.status === "error" && row.error_message && (
                      <div className="text-xs text-muted-foreground mt-1">{row.error_message}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {!hasLoadError && (
          <TablePagination
            totalItems={data?.total ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        )}
      </div>
    </div>
  );
}
